import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, Printer, Camera } from 'lucide-react';
import { useHiddenStudentRoute } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

function IATest2Report() {
  const { rollNumber, navigate } = useHiddenStudentRoute('/student/ia-test2-report');
  const reportRef = useRef();
  
  const [student, setStudent] = useState(null);
  const [iaTest1, setIaTest1] = useState(null);
  const [iaTest2, setIaTest2] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadData();
  }, [rollNumber]);

  // ✅ Fetch mentor and coordinator names from their respective collections
  const fetchMentorAndCoordinator = async (batch, division) => {
    try {
      let mentorName = 'N/A';
      let ccName = 'N/A';

      // Fetch mentor name from batchAssignments using batch as document ID
      if (batch) {
        const batchDoc = await getDoc(doc(db, 'batchAssignments', batch));
        if (batchDoc.exists()) {
          mentorName = batchDoc.data().mentorName || 'N/A';
        }
      }

      // Fetch coordinator name from divisionAssignments using division as document ID
      if (division) {
        const divisionDoc = await getDoc(doc(db, 'divisionAssignments', division));
        if (divisionDoc.exists()) {
          const divisionData = divisionDoc.data();
          ccName = divisionData.ccName || divisionData.coordinatorName || 'N/A';
        }
      }

      return { mentorName, ccName };
    } catch (error) {
      console.error('Error fetching mentor/coordinator:', error);
      return { mentorName: 'N/A', ccName: 'N/A' };
    }
  };

  const loadData = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        
        // ✅ Fetch mentor and coordinator names based on batch and division
        const { mentorName, ccName } = await fetchMentorAndCoordinator(
          studentData.batch,
          studentData.division
        );
        
        // Update student data with fetched names
        setStudent({
          ...studentData,
          mentorName: mentorName,
          ccName: ccName
        });
      }

      const iaTest1Doc = await getDoc(doc(db, 'iaTest1', rollNumber));
      if (iaTest1Doc.exists()) {
        setIaTest1(iaTest1Doc.data());
      }

      const iaTest2Doc = await getDoc(doc(db, 'iaTest2', rollNumber));
      if (iaTest2Doc.exists()) {
        setIaTest2(iaTest2Doc.data());
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!student || !iaTest2) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Data not found</p>
      </div>
    );
  }

  const subjects = [
    { code: 'AM-I', name: 'Applied Mathematics-I', total: 20, marks: iaTest2.subjects?.amI || 0 },
    { code: 'AP', name: 'Applied Physics', total: 15, marks: iaTest2.subjects?.ap || 0 },
    { code: 'AC', name: 'Applied Chemistry', total: 15, marks: iaTest2.subjects?.ac || 0 },
    { code: 'EM', name: 'Engineering Mechanics', total: 20, marks: iaTest2.subjects?.em || 0 },
    { code: 'BEE', name: 'Basic Electrical and Electronics Engg', total: 20, marks: iaTest2.subjects?.bee || 0 },
    { code: 'PCE', name: 'Professional Communication and Ethics', total: 15, marks: iaTest2.subjects?.pce || 0 }
  ];

  const averagePercentage = iaTest1 && iaTest2 ? ((iaTest1.percentage + iaTest2.percentage) / 2).toFixed(2) : iaTest2.percentage.toFixed(2);

  return (
    <div className="min-h-screen bg-gray-100 py-4 print:bg-white print:py-0">
      <div className="max-w-[210mm] mx-auto px-4 print:px-0">
        
        {/* Action Buttons */}
        <div className="flex justify-between mb-4 print:hidden">
          <button 
            onClick={() => navigate('/student/dashboard')} 
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
          <button 
            onClick={handlePrint} 
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Printer className="w-4 h-4" />
            <span>Print Report</span>
          </button>
        </div>

        {/* A4 Report */}
        <div 
          ref={reportRef}
          className="bg-white shadow-lg print:shadow-none"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '15mm',
            margin: '0 auto'
          }}
        >
          {/* Letterhead */}
          <div className="mb-3">
            <img 
              src="/ybit-letterhead.png" 
              alt="YBIT Letterhead" 
              className="w-full h-auto object-contain"
              style={{ maxHeight: '90px' }}
            />
          </div>
          
          <div className="border-b-2 border-black mb-4"></div>

          {/* Report Title */}
          <h2 className="text-xl font-bold text-center mb-4 uppercase">Internal Assessment - 2</h2>

          {/* Student Info with Photo */}
          <div className="grid grid-cols-4 gap-4 mb-4 pb-3 border-b border-gray-300">
            <div className="col-span-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="font-semibold">Name:</span> {student.firstName} {student.surname}
                </div>
                <div>
                  <span className="font-semibold">Roll Number:</span> {rollNumber}
                </div>
                <div>
                  <span className="font-semibold">Division:</span> {student.division}
                </div>
                <div>
                  <span className="font-semibold">Batch:</span> {student.batch}
                </div>
                <div>
                  <span className="font-semibold">Mentor:</span> {student.mentorName || 'Not Assigned'}
                </div>
                <div>
                  <span className="font-semibold">Class Coordinator:</span> {student.ccName || 'Not Assigned'}
                </div>
              </div>
            </div>
            
            {/* Student Photo */}
            <div className="col-span-1 flex justify-center">
              <div className="w-24 h-24 border-2 border-gray-300 rounded overflow-hidden bg-gray-100">
                {getStudentPhotoUrl(student) ? (
                  <img 
                    src={getStudentPhotoUrl(student)}  
                    alt="Student" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Marks Table */}
          <table className="w-full border-collapse mb-4">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="border border-gray-300 px-4 py-2 text-left">Course Name</th>
                <th className="border border-gray-300 px-4 py-2 text-center w-32">Total Marks</th>
                <th className="border border-gray-300 px-4 py-2 text-center w-32">Marks Obtained</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border border-gray-300 px-4 py-2">{subject.code} - {subject.name}</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{subject.total}</td>
                  <td className={`border border-gray-300 px-4 py-2 text-center font-semibold ${
                    subject.marks < subject.total * 0.4 ? 'text-red-600' : 'text-blue-600'
                  }`}>
                    {subject.marks}
                  </td>
                </tr>
              ))}
              
              {/* Total */}
              <tr className="bg-blue-100 font-bold text-lg">
                <td className="border border-gray-300 px-4 py-2">TOTAL</td>
                <td className="border border-gray-300 px-4 py-2 text-center">105</td>
                <td className="border border-gray-300 px-4 py-2 text-center">{iaTest2.total}</td>
              </tr>
              
              {/* Percentage */}
              <tr className="bg-blue-600 text-white font-bold text-lg">
                <td className="border border-gray-300 px-4 py-3" colSpan="2">PERCENTAGE</td>
                <td className="border border-gray-300 px-4 py-3 text-center">
                  {Number(iaTest2.percentage).toFixed(2)}%
                </td>
              </tr>
            </tbody>
          </table>

          {/* Performance Comparison */}
          {iaTest1 && (
            <div className="mb-4">
              <h3 className="font-bold text-gray-900 mb-3 text-sm">📈 Performance Comparison</h3>
              <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-gray-600 text-xs">IA-1 Performance</p>
                  <p className="text-xl font-bold text-blue-600">{Number(iaTest1.percentage).toFixed(2)}%</p>
                </div>
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <p className="text-gray-600 text-xs">IA-2 Performance</p>
                  <p className="text-xl font-bold text-green-600">{Number(iaTest2.percentage).toFixed(2)}%</p>
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded text-center border border-purple-200">
                <p className="text-gray-600 mb-1 text-xs">Average Performance</p>
                <p className="text-2xl font-bold text-purple-600">{averagePercentage}%</p>
              </div>
            </div>
          )}

          {/* Remarks */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
            <h3 className="font-bold text-blue-900 mb-2 text-sm">📊 Performance Remarks - Internal Assessment</h3>
            <div className="text-sm space-y-1">
              {iaTest1 && (
                <>
                  {averagePercentage >= 85 ? (
                    <p className="text-blue-800">🌟 <strong>Outstanding performance!</strong> You have achieved an excellent average of {averagePercentage}% across your internal assessments. Keep up the great work.</p>
                  ) : averagePercentage >= 70 ? (
                    <p className="text-blue-800">👍 <strong>Good performance!</strong> Your average is {averagePercentage}%. Continue your efforts!</p>
                  ) : averagePercentage >= 50 ? (
                    <p className="text-yellow-800">⚠️ <strong>Average performance.</strong> Your average is {averagePercentage}%. Focus on improvement areas.</p>
                  ) : (
                    <p className="text-red-800">❌ <strong>Needs improvement.</strong> Your average is {averagePercentage}%. Please consult with your mentor.</p>
                  )}
                  
                  {iaTest2.percentage > iaTest1.percentage ? (
                    <p className="text-green-800">📈 <strong>Improvement noted!</strong> Your performance increased from IA-1 ({Number(iaTest1.percentage).toFixed(2)}%) to IA-2 ({Number(iaTest2.percentage).toFixed(2)}%). Excellent progress!</p>
                  ) : iaTest2.percentage === iaTest1.percentage ? (
                    <p className="text-blue-800">📊 <strong>Consistent performance!</strong> You maintained {Number(iaTest2.percentage).toFixed(2)}% in both tests. Keep up the consistency!</p>
                  ) : (
                    <p className="text-yellow-800">📉 <strong>Needs attention.</strong> Your performance decreased from IA-1 ({Number(iaTest1.percentage).toFixed(2)}%) to IA-2 ({Number(iaTest2.percentage).toFixed(2)}%). Discuss with your mentor for improvement strategies.</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Signature Section */}
          <div className="mt-12 pt-6 border-t-2 border-gray-300">
            <div className="grid grid-cols-3 gap-8 text-center text-sm">
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-16"></div>
                <p className="font-semibold">Class Coordinator Sign</p>
               
              </div>
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-16"></div>
                <p className="font-semibold">HOD Sign</p>
                
              </div>
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-16"></div>
                <p className="font-semibold">Principal</p>
                
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-[9px] text-gray-600 mt-6 pt-4 border-t border-gray-300">
            <p>Bhonsale Knowledge City, Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi, Dist: Sindhudurg 416510</p>
            <p>DTE CODE: 3470 | MSBTE CODE: 1742 | Generated on: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}

export default IATest2Report;
